import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Autocomplete,
  useTheme,
  alpha,
  styled,
} from '@mui/material';
import { UserMinus, Users, Calendar, X, Search } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import userClientAccessService, { AuthGuardUser, UserAccess } from '../services/api/userClientAccessService';
import { getAuthGuardResourceUsers, AuthGuardUserProfile } from '../services/usersService';

interface ClientAccessControlModalProps {
  open: boolean;
  onClose: (updatedUserCount?: number) => void;
  clientId: number;
  clientName: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    minWidth: '600px',
    maxWidth: '800px',
  },
}));


const ClientAccessControlModal: React.FC<ClientAccessControlModalProps> = ({
  open,
  onClose,
  clientId,
  clientName,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [currentUsers, setCurrentUsers] = useState<UserAccess[]>([]);
  const [allAuthGuardUsers, setAllAuthGuardUsers] = useState<AuthGuardUserProfile[]>([]);
  const [authGuardUsersFetched, setAuthGuardUsersFetched] = useState(false);
  const [searchResults, setSearchResults] = useState<AuthGuardUserProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AuthGuardUserProfile | null>(null);
  const [processing, setProcessing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // Reset cache when modal closes so we fetch fresh data next time
      setAllAuthGuardUsers([]);
      setAuthGuardUsersFetched(false);
      setSearchResults([]);
      setSearchInput('');
      setDebouncedSearch('');
      setSelectedUser(null);
    }
  }, [open, clientId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Perform search when debounced value changes
  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 2) {
      performSearch(debouncedSearch);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearch, currentUsers, allAuthGuardUsers, authGuardUsersFetched]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Only load current users with access
      const usersResponse = await userClientAccessService.getClientUsers(clientId, true);
      setCurrentUsers(usersResponse.users);
    } catch (error) {
      console.error('Error loading user access data:', error);
      showToast('Failed to load user access data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to filter search results based on current users
  const filterSearchResults = useCallback((searchTerm: string, users: AuthGuardUserProfile[], currentUsersList: UserAccess[]) => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    return users.filter((user) => {
      const name = user.personalInformation
        ? `${user.personalInformation.firstName} ${user.personalInformation.lastName}`.toLowerCase()
        : user.profileInformation.profileName.toLowerCase();
      const email = user.personalInformation?.email?.toLowerCase() || '';
      
      // Exclude users who already have access
      const hasAccess = currentUsersList.some(
        (access) => access.user_email.toLowerCase() === email
      );
      
      return !hasAccess && (name.includes(searchLower) || email.includes(searchLower));
    });
  }, []);

  const performSearch = useCallback(async (searchTerm: string) => {
    // Fetch AuthGuard users only once and cache them
    if (!authGuardUsersFetched) {
      setSearching(true);
      try {
        const response = await getAuthGuardResourceUsers('serp', true);
        setAllAuthGuardUsers(response.users);
        setAuthGuardUsersFetched(true);
        
        // Filter with newly fetched users
        const filtered = filterSearchResults(searchTerm, response.users, currentUsers);
        setSearchResults(filtered);
      } catch (error) {
        console.error('Error fetching AuthGuard users:', error);
        showToast('Failed to load users from AuthGuard', 'error');
      } finally {
        setSearching(false);
      }
    } else {
      // Filter cached results
      const filtered = filterSearchResults(searchTerm, allAuthGuardUsers, currentUsers);
      setSearchResults(filtered);
    }
  }, [authGuardUsersFetched, allAuthGuardUsers, currentUsers, filterSearchResults, showToast]);

  const handleAddUser = async (user: AuthGuardUserProfile | null) => {
    if (!user || !user.personalInformation?.email) {
      return;
    }

    setProcessing(true);
    try {
      const userName = user.personalInformation
        ? `${user.personalInformation.firstName} ${user.personalInformation.lastName}`
        : user.profileInformation.profileName;

      await userClientAccessService.grantAccess(
        user.personalInformation.email,
        clientId,
        userName
      );
      showToast('Access granted successfully', 'success');
      setSelectedUser(null);
      setSearchInput('');
      
      // Reload current users list - search results will update via useEffect
      const response = await userClientAccessService.getClientUsers(clientId);
      setCurrentUsers(response.users);
    } catch (error) {
      console.error('Error granting access:', error);
      showToast('Failed to grant access', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeAccess = async (userEmail: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to revoke access for ${userName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await userClientAccessService.revokeAccess(userEmail, clientId);
      showToast('Access revoked successfully', 'success');
      
      // Reload current users list - search results will update via useEffect
      const response = await userClientAccessService.getClientUsers(clientId);
      setCurrentUsers(response.users);
    } catch (error) {
      console.error('Error revoking access:', error);
      showToast('Failed to revoke access', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };


  return (
    <StyledDialog 
      open={open} 
      onClose={() => onClose(currentUsers.length)} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '700px',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={24} color={theme.palette.primary.main} />
            <Box>
              <Typography variant="h6" component="div">
                Client Access Control
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {clientName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => onClose(currentUsers.length)} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header with Search Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, pt: 1, flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                Users with Access ({currentUsers.length})
              </Typography>
              
              {/* Add User Search Field */}
              <Box sx={{ flex: 1, maxWidth: 400 }}>
                <Autocomplete
                  value={selectedUser}
                  inputValue={searchInput}
                  onInputChange={(event, newInputValue) => {
                    setSearchInput(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    handleAddUser(newValue);
                  }}
                  options={searchResults}
                  getOptionLabel={(option) => {
                    const name = option.personalInformation
                      ? `${option.personalInformation.firstName} ${option.personalInformation.lastName}`
                      : option.profileInformation.profileName;
                    const email = option.personalInformation?.email || '';
                    return `${name} (${email})`;
                  }}
                  filterOptions={(x) => x}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const name = option.personalInformation
                      ? `${option.personalInformation.firstName} ${option.personalInformation.lastName}`
                      : option.profileInformation.profileName;
                    const email = option.personalInformation?.email || '';
                    
                    return (
                      <Box key={key} component="li" {...otherProps}>
                        <Box>
                          <Typography variant="body1">{name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {email}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Add user..."
                      disabled={processing}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  loading={searching}
                  disabled={processing}
                  noOptionsText={searchInput.length < 2 ? "Type to search..." : "No users found"}
                />
              </Box>
            </Box>

            {/* Assigned Users List */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {currentUsers.length === 0 ? (
                <Alert severity="info">
                  No users have access to this client yet. Use the search field to add users.
                </Alert>
              ) : (
                <List
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: 2,
                  }}
                >
                  {currentUsers.map((userAccess, index) => {
                    const userName = userAccess.user_name || userAccess.user_email;
                    const grantedByName = userAccess.granted_by_name || userAccess.granted_by_email;
                    
                    return (
                      <React.Fragment key={userAccess.access_id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" fontWeight={500}>
                                  {userName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ({userAccess.user_email})
                                </Typography>
                                <Chip
                                  label="Active"
                                  size="small"
                                  color="success"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  mt: 0.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Calendar size={12} color={theme.palette.text.secondary} />
                                  <Typography variant="caption" color="text.secondary">
                                    Granted: {formatDate(userAccess.granted_at)}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  By: {grantedByName}
                                </Typography>
                                {userAccess.notes && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic' }}
                                  >
                                    • {userAccess.notes}
                                  </Typography>
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleRevokeAccess(userAccess.user_email, userName)
                              }
                              disabled={processing}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <UserMinus size={18} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          px: 3,
          py: 2,
          flexShrink: 0,
        }}
      >
        <Button onClick={() => onClose(currentUsers.length)} disabled={processing}>
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ClientAccessControlModal;
